import {
  differenceInHours,
  format,
  formatDistanceToNow,
  isToday,
} from 'date-fns';

import { HOURS_IN_DAY, ZERO } from '@/constants';
import { CardType, SurveyResponseStatus, SurveyStatus } from '@/types';

import type {
  ClientEvent,
  Event,
  EventStatus,
  HomeCardPropsWithoutCallbacks,
  SessionDashboard,
  Survey,
  User,
} from '@/types';
import type { ButtonProps } from '@/ui';

export const getStatus = (event: Event) => {
  const now = new Date();
  let label: EventStatus = 'published';

  if (!event.startedAt) {
    label = 'draft';
  }

  if (event.endedAt && now > new Date(event.endedAt)) {
    label = 'expired';
  }

  return label;
};

export const getEventCardProps = (
  event: Event,
): HomeCardPropsWithoutCallbacks => {
  const actions = [];

  if (event.ctaLabel) {
    actions.push({
      buttonProps: {
        color: (event.isDarkTheme
          ? 'white'
          : 'primary') as ButtonProps['color'],
        ...(event.ctaLink && { href: event.ctaLink }),
      },
      label: event.ctaLabel,
    });
  }

  return {
    actions,
    background: !event.isImageInline ? event.imageUrl : undefined,
    children: event.body,
    id: event.id,
    isDarkTheme: event.isDarkTheme,
    isDismissable: event.isDismissable,
    label: event.label || '',
    status: getStatus(event),
    title: event.title,
    type: CardType.EVENT,
  };
};

export const getMatchingCoachCardProps = (): HomeCardPropsWithoutCallbacks => ({
  background: 1,
  children: { message: "We're on it-this could take 2 to 3 days" },
  id: CardType.MATCHING_COACH,
  label: 'Coaching',
  title: 'Matching you with the right coach',
  type: CardType.MATCHING_COACH,
});

export const getMatchedWithCoachCardProps =
  (): HomeCardPropsWithoutCallbacks => ({
    background: 4,
    children:
      'Unlock your growth with personalized 1-on-1 coaching from industry leaders.',
    className: 'bg-none',
    id: CardType.GET_MATCHED,
    imageUrl: '',
    label: 'Mento Coaching',
    title: 'Get matched with a Coach',
    type: CardType.GET_MATCHED,
  });

export const getCoachSessionsCardProps = ({
  now = Date.now(),
  user,
  sessionsData,
}: {
  now?: number;
  sessionsData: SessionDashboard;
  user: User;
}): HomeCardPropsWithoutCallbacks[] => {
  const result: HomeCardPropsWithoutCallbacks[] = [];
  const endedChemistryCoachIds = new Set(
    Object.entries(sessionsData.upcoming)
      .filter(([, session]) => {
        const endedAt = Date.parse(session.endedAt);
        return (
          session.isChemistryCall && Number.isFinite(endedAt) && endedAt < now
        );
      })
      .map(([coachId]) => coachId),
  );
  const completedChemistryCoachIds = [...endedChemistryCoachIds].filter(
    (coachId) => !sessionsData.activeBiweekly?.[coachId],
  );

  // No sessions cards. The dashboard lists coach ids, and a coach with both an
  // ended and an open assignment has been observed twice in the same list
  // (MEN-5025), which rendered a duplicate chemistry CTA. One card per coach.
  new Set(sessionsData.noSessions).forEach((id) => {
    const coach = user.coaches.find((c) => c.id === id);
    if (!coach) {
      return;
    }

    result.push({
      background: 1,
      children: {
        avatarUrl: coach.avatarUrl,
        firstName: coach.fullName || coach.firstName,
        lastName: coach.lastName as string,
        message: `Nice to meet you ${user.firstName}! Tap the button to find time for our first session.`,
      },
      coach,
      // Ensure unique and stable id per coach
      id: `${CardType.MEET_COACH}-${coach.id}`,
      label: 'Coaching',
      title: `Meet your Mento Coach, ${coach.firstName || coach.firstName}`,
      type: CardType.MEET_COACH,
      user,
    });
  });

  // No upcoming sessions cards
  new Set([...sessionsData.noUpcoming, ...completedChemistryCoachIds]).forEach(
    (id) => {
      const coach = user.coaches.find((c) => c.id === id);
      if (!coach) {
        return;
      }

      result.push({
        background: 1,
        children: {
          avatarUrl: coach.avatarUrl,
          firstName: coach.fullName || coach.firstName,
          lastName: coach.lastName as string,
          message: `Hey ${user.firstName}. Tap the schedule button to schedule our next session.`,
        },
        coach,
        // Ensure unique and stable id per user+coach
        id: `${CardType.PREVIOUS_SESSION}-${user.id}-${coach.id}`,
        label: 'Coaching',
        title: `No upcoming sessions with ${coach.firstName}`,
        type: CardType.PREVIOUS_SESSION,
        user,
      });
    },
  );

  // Sessions cards
  Object.entries(sessionsData.upcoming).forEach(([sessionCoachId, session]) => {
    if (endedChemistryCoachIds.has(sessionCoachId)) return;
    const coach = user.coaches.find(
      ({ id: coachId }) => coachId === sessionCoachId,
    );
    if (!coach) {
      return;
    }

    const startedAt = new Date(session.startedAt);
    const isSessionToday = isToday(startedAt);
    const distance = differenceInHours(startedAt, new Date());

    let title = '';

    if (isSessionToday) {
      title = `Today at ${format(startedAt, 'h:mmaaa')} with ${
        coach.firstName || ''
      }`;
    } else if (distance <= HOURS_IN_DAY) {
      title = 'Your session is tomorrow';
    } else {
      title = `Next session in ${formatDistanceToNow(startedAt)}`;
    }

    if (session.isChemistryCall) {
      result.push({
        background: 12,
        coach,
        // Ensure unique id per session
        id: `${CardType.NEXT_SESSION}-${session.id}`,
        isSessionToday,
        label: 'Coaching',
        session,
        title: title.replace(
          /(.*?)(at|in|is)\s(\w+)/u,
          'Chemistry call $2 $3 ',
        ),
        type: CardType.NEXT_SESSION,
        user,
      });
    } else {
      result.push({
        background: 2,
        coach,
        // Ensure unique id per session
        id: `${CardType.NEXT_SESSION}-${session.id}`,
        isSessionToday,
        label: 'Coaching',
        session,
        title,
        type: CardType.NEXT_SESSION,
      });
    }
  });

  return result;
};

const getPeerInsightAwarenessCard = (
  event: ClientEvent,
): HomeCardPropsWithoutCallbacks => ({
  background: 7,
  children:
    'Turn Strengths and Opportunities feedback from your peers into action with your coach.',
  id: event.key,
  isDarkTheme: true,
  label: 'Insights',
  title: 'Get insights from peers to shape your growth',
  type: event.key,
});

const getPeerInsightCard = (
  event: ClientEvent,
  peerInsight: Survey,
): HomeCardPropsWithoutCallbacks | undefined => {
  const invitedCount = peerInsight.responses?.length ?? ZERO;
  const completedCountFromResponses =
    peerInsight.responses?.filter(
      (response) => response.status === SurveyResponseStatus.COMPLETED,
    ).length ?? ZERO;
  const respondedCount =
    peerInsight.aggregated?.totalResponses ?? completedCountFromResponses;
  let children = 'Unknown State';
  let title = 'Peer Insights';
  let isDismissable = false;

  if (event.key === CardType.PEER_INSIGHT) {
    if (peerInsight.status === SurveyStatus.DRAFT) {
      title = 'Pick up where you left off';
      children = invitedCount
        ? `You added ${invitedCount} peers. Next, send them a short survey.`
        : "Add people you'd like to get";
      isDismissable = true;
    } else if (peerInsight.status === SurveyStatus.SHARED) {
      title = 'Insights are ready!';
      children = 'Your coach shared your peer insights with you.';
      isDismissable = true;
    } else {
      if (respondedCount) {
        title =
          invitedCount && invitedCount >= respondedCount
            ? `${respondedCount} of ${invitedCount} peers responded`
            : `${respondedCount} ${respondedCount === 1 ? 'peer' : 'peers'} responded`;
      } else {
        title = 'Waiting on responses';
      }

      children = respondedCount
        ? 'Your coach will walk you through your insights in a future session.'
        : "We'll keep you updated as responses come in.";
    }

    return {
      background: peerInsight.status === SurveyStatus.SHARED ? 11 : 10,
      children,
      dismissedTill: event.dismissedTill,
      id: event.key,
      isDismissable,
      label: 'Peer Insights',
      peerInsight,
      title,
      type: event.key,
    };
  }
  return undefined;
};

export const getClientEventsCardsProps = (
  events: ClientEvent[],
  peerInsight: Survey | null,
  user: User,
): HomeCardPropsWithoutCallbacks[] => {
  const order = [
    CardType.PEER_INSIGHT_AWARENESS,
    CardType.PEER_INSIGHT,
    CardType.LEARN_ABOUT,
    CardType.CREATE_PROFILE,
    CardType.GROWTH_PLAN,
  ];

  // HORRIBLE HACK INCOMING. Explanation after it.
  const fakeClientEvents: ClientEvent[] = [
    ...(events.find((e) => e.key === CardType.LEARN_ABOUT)
      ? []
      : [{ key: CardType.LEARN_ABOUT } as ClientEvent]),
    ...(events.find((e) => e.key === CardType.CREATE_PROFILE)
      ? []
      : [{ key: CardType.CREATE_PROFILE } as ClientEvent]),
    ...(events.find((e) => e.key === CardType.GROWTH_PLAN)
      ? []
      : [{ key: CardType.GROWTH_PLAN } as ClientEvent]),
    ...(events.find((e) => e.key === CardType.PEER_INSIGHT_AWARENESS)
      ? []
      : [{ key: CardType.PEER_INSIGHT_AWARENESS } as ClientEvent]),
    ...(events.find((e) => e.key === CardType.PEER_INSIGHT)
      ? []
      : [{ key: CardType.PEER_INSIGHT } as ClientEvent]),
  ];
  const clientEventsCopy = [...events, ...fakeClientEvents];
  // So... since the card refactor we've figured out that first-time
  // users don't get the `clientEvents` generated on the backend
  // and end up with a completely empty dashboard.
  //
  // The real fix is to fix the backend to generate these clientEvents.
  // However, for now, we'll be faking these events on the frontend if
  // the clientEvents are empty.
  //
  // Once this is fixed, we can replace the below line and
  // delete the clientEventsCopy const.
  //
  // const sorted = [...events].sort(
  const sorted = clientEventsCopy.sort(
    (a, b) => order.indexOf(a.key) - order.indexOf(b.key),
  );
  const now = new Date().getTime();
  const isNotDismissed = (dismissedTill: ClientEvent['dismissedTill']) =>
    !dismissedTill || now > new Date(dismissedTill as string).getTime();

  return sorted.reduce<HomeCardPropsWithoutCallbacks[]>((aggr, event) => {
    const isViewed = !!event.viewedAt;
    const shouldShow = (key: CardType): CardType | undefined => {
      let show = false;

      switch (key) {
        case CardType.PEER_INSIGHT_AWARENESS:
          show = !peerInsight;
          break;
        case CardType.PEER_INSIGHT:
          show = !!peerInsight && isNotDismissed(event.dismissedTill);
          break;
        case CardType.LEARN_ABOUT:
          show = !isViewed;
          break;
        case CardType.CREATE_PROFILE:
          show = !isViewed && isNotDismissed(event.dismissedTill);
          break;
        case CardType.ADD_PROFILE_IMAGE:
          show = !user.avatarUrl && isNotDismissed(event.dismissedTill);
          break;
        case CardType.GROWTH_PLAN:
          show = isNotDismissed(event.dismissedTill);
          break;
        default:
          show = false;
      }

      return show ? key : undefined;
    };
    let card;

    switch (shouldShow(event.key)) {
      case CardType.PEER_INSIGHT_AWARENESS:
        aggr.push(getPeerInsightAwarenessCard(event));
        break;
      case CardType.PEER_INSIGHT:
        if (peerInsight) {
          card = getPeerInsightCard(event, peerInsight);
          if (card) {
            aggr.push(card);
          }
        }
        break;
      case CardType.LEARN_ABOUT:
        aggr.push({
          background: 8,
          children:
            'Learn about our coaching approach, what to expect next, and how to use Mento.',
          id: event.key,
          label: 'Get started',
          title: 'Learn about Mento',
          type: event.key,
        });
        break;
      case CardType.CREATE_PROFILE:
        aggr.push({
          background: 4,
          children:
            'The more info you provide, the better your Coach is set up to support your growth.',
          dismissedTill: event.dismissedTill,
          id: event.key,
          isDismissable: true,
          label: 'Get started',
          title: 'Create your profile',
          type: event.key,
        });
        break;
      case CardType.ADD_PROFILE_IMAGE:
        aggr.push({
          background: 4,
          children: '',
          dismissedTill: event.dismissedTill,
          id: event.key,
          isDismissable: true,
          label: 'Get started',
          title: 'Add profile photo',
          type: event.key,
          user,
        });
        break;
      case CardType.GROWTH_PLAN:
        aggr.push({
          background: 5,
          children: '',
          id: event.key,
          label: 'Growth plan',
          title: '',
          type: event.key,
        });
        break;
      default:
        break;
    }
    return aggr;
  }, []);
};
